// src/modules/clinical-notes-v2/components/CodingOpSuggestionCard.jsx
//
// Card de coding opportunity. Misma estructura visual que
// CareGapSuggestionCard pero paleta secondary (purple). El plan de
// acción para coding ops incluye exámenes recomendados + M.E.A.T. +
// HPI addition + Assessment + Plan + ICD-10 specificity.

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
  Button,
} from "@mui/material";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon  from "@mui/icons-material/ExpandMore";
import ExpandLessIcon  from "@mui/icons-material/ExpandLess";
import NorthIcon       from "@mui/icons-material/North";
import ScienceIcon     from "@mui/icons-material/Science";

const STATUS_COLORS = {
  open:      { bg: "#f3e5f5", border: "#8e24aa", chip: "secondary", label: "OPEN" },
  submitted: { bg: "#fff8e1", border: "#fb8c00", chip: "warning",   label: "SUBMITTED" },
  closed:    { bg: "#e8f5e9", border: "#43a047", chip: "success",   label: "CLOSED" },
};

const EXAM_TYPE_LABELS = { lab: "Lab", imaging: "Imaging", ecg: "ECG", other: "Other" };

const resolveStatusKey = (op) => {
  const k = (op?.status || "Open").toString().toUpperCase();
  if (k.includes("SUBMIT")) return "submitted";
  if (k.includes("CLOSE"))  return "closed";
  return "open";
};

export default function CodingOpSuggestionCard({
  op,
  selected,
  onToggleSelected,
  suggestionState,
  onRequestSuggestion,
  onApplyPlanToDraft,
  aiEnabled = true,
  disabled = false,
}) {
  const status    = suggestionState?.status || "idle";
  const isReady   = status === "ready";
  const statusKey = resolveStatusKey(op);
  const palette   = STATUS_COLORS[statusKey];

  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => { if (isReady) setExpanded(true); }, [isReady]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `5px solid ${palette.border}`,
        borderRadius: 2,
        transition: "transform .2s, box-shadow .2s",
        bgcolor: selected ? palette.bg : "background.paper",
        "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
      }}
    >
      <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Checkbox
            edge="start"
            checked={!!selected}
            onChange={(e) => onToggleSelected(op.codingOpportunityId, e.target.checked)}
            disabled={disabled}
            size="small"
            sx={{ mt: -0.5 }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
              <Chip size="small" color={palette.chip} label={palette.label} sx={{ fontWeight: 800, height: 20 }} />
              {isReady && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  color="secondary"
                  variant="outlined"
                  label="Plan ready"
                  sx={{ height: 20 }}
                />
              )}
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25 }} title={op.suspectedDescription}>
              {op.suspectedDescription || op.suspectedIcd10 || "—"}
            </Typography>

            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.5 }}>
              {op.suspectedIcd10 && <Chip size="small" variant="outlined" color="secondary" label={op.suspectedIcd10} />}
              {op.hccCategory   && <Chip size="small" variant="outlined" label={op.hccCategory} />}
              {op.rafImpact != null && (
                <Chip size="small" variant="outlined" color="success" label={`RAF +${Number(op.rafImpact).toFixed(3)}`} />
              )}
              {Array.isArray(op.evidence) && op.evidence.length > 0 && (
                <Chip size="small" variant="outlined" label={`${op.evidence.length} evidence`} />
              )}
            </Stack>
          </Box>

          {aiEnabled && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant={isReady ? "outlined" : "contained"}
                color="secondary"
                startIcon={status === "loading" ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={() => onRequestSuggestion(op)}
                disabled={disabled || status === "loading"}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {status === "loading" ? "Generating…" : isReady ? "Regenerate" : "Plan of action"}
              </Button>
              {isReady && (
                <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: "secondary.main" }}>
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>

        {status === "error" && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {suggestionState?.error || "Could not generate suggestion."}
          </Typography>
        )}

        {aiEnabled && (
          <Collapse in={isReady && expanded} timeout="auto" unmountOnExit>
            <InlineActionPlan
              data={suggestionState?.data}
              onApply={() => onApplyPlanToDraft?.(op, suggestionState?.data)}
            />
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
}

function InlineActionPlan({ data, onApply }) {
  const sug = data?.suggestion;
  if (!sug) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "#f3e5fd",
        border: "1px solid #ce93d8",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: "#7b1fa2" }} />
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#4a148c", flex: 1 }}>
          Action plan
        </Typography>
        {onApply && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<NorthIcon />}
            onClick={onApply}
            sx={{ textTransform: "none" }}
          >
            Apply to draft
          </Button>
        )}
      </Stack>

      {Array.isArray(sug.examsRecommended) && sug.examsRecommended.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "#4a148c", display: "block", mb: 0.5 }}>
            Exams recommended
          </Typography>
          <Stack spacing={0.75}>
            {sug.examsRecommended.map((ex, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                <ScienceIcon fontSize="small" sx={{ mt: 0.25, color: "text.secondary" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {EXAM_TYPE_LABELS[ex.type] || ex.type || "—"} · {ex.name}
                  </Typography>
                  {ex.rationale && (
                    <Typography variant="caption" color="text.secondary">{ex.rationale}</Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {sug.meatDocumentation && (
        <Box sx={{ mb: 1.5, p: 1, bgcolor: "#fafafa", borderRadius: 1, fontFamily: "monospace", fontSize: 12 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "#4a148c", display: "block", mb: 0.5, fontFamily: "inherit" }}>
            M.E.A.T. documentation
          </Typography>
          {[
            ["Monitor", sug.meatDocumentation.monitor],
            ["Evaluate", sug.meatDocumentation.evaluate],
            ["Assess", sug.meatDocumentation.assess],
            ["Treat", sug.meatDocumentation.treat],
          ].map(([label, text]) => text ? (
            <Box key={label} sx={{ mb: 0.25 }}>
              <Typography component="span" variant="caption" sx={{ fontWeight: 800 }}>{label}: </Typography>
              <Typography component="span" variant="caption">{text}</Typography>
            </Box>
          ) : null)}
        </Box>
      )}

      <PlanBlock label="HPI addition"  value={sug.hpiAddition} />
      <PlanBlock label="Assessment"    value={sug.assessment} />
      <PlanBlock label="Plan"          list={sug.plan} />
      <PlanBlock label="ICD-10 specificity" value={sug.icd10Specificity} />
    </Box>
  );
}

function PlanBlock({ label, value, list }) {
  const hasValue = !!value?.trim?.();
  const hasList  = Array.isArray(list) && list.length > 0;
  if (!hasValue && !hasList) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: "#4a148c", display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      {hasValue && (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value}</Typography>
      )}
      {hasList && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {list.map((it, i) => (
            <li key={i}><Typography variant="body2">{it}</Typography></li>
          ))}
        </Box>
      )}
    </Box>
  );
}
